import { logger } from './logger';

/** @desc sessionStorage key holding when this tab last reloaded for a chunk that would not load. */
export const PRELOAD_RELOAD_KEY = 'h2gc:preload-error-reload';

/**
 * @desc A second chunk failure this soon after a reload means the reload did not help — the
 * asset is broken, not stale — so the page stops reloading and lets the error boundary offer
 * Reload instead. Past the window a fresh failure is a fresh deployment and may reload again.
 */
export const PRELOAD_RELOAD_WINDOW_MS = 10 * 60_000;

export type PreloadReloadEnv = {
  /** @desc `undefined` where the browser blocks storage: without it there is no loop guard. */
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined;
  now: () => number;
  online: () => boolean;
  reload: () => void;
};

/** @desc A full page reload — one seam, so the boundaries' Reload buttons and this handler share it. */
export const reloadPage = (): void => window.location.reload();

const browserStorage = (): PreloadReloadEnv['storage'] => {
  try {
    return window.sessionStorage;
  } catch {
    // Reading `sessionStorage` itself throws where site data is blocked; treated as "no guard".
    return undefined;
  }
};

const browserEnv = (): PreloadReloadEnv => ({
  storage: browserStorage(),
  now: () => Date.now(),
  online: () => window.navigator.onLine,
  reload: reloadPage,
});

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error));

/**
 * @desc Vite raises `vite:preloadError` when a dynamic import or one of its preloaded
 * dependencies (JS or CSS) fails to load. In production the usual cause is a deployment that
 * replaced the chunk hashes this tab's entry still names: the old file is gone, the SPA rewrite
 * answers with `index.html`, and the import fails. Reloading picks up the new entry, so this
 * reloads — at most once per {@link PRELOAD_RELOAD_WINDOW_MS}, guarded in sessionStorage so a
 * chunk that fails after the reload too cannot loop.
 *
 * It never calls `preventDefault`: the import still rejects, so the boundary that would have
 * caught it (a route's error component, `LazyBoundary`) still does — briefly, while the reload
 * happens, or for good when the reload is skipped. Skipped when offline (a reload would only
 * swap the page for the browser's offline screen) and when storage is unavailable (no guard).
 *
 * @returns whether a reload was started.
 */
export function handlePreloadError(error: unknown, env: PreloadReloadEnv = browserEnv()): boolean {
  if (!env.online()) {
    logger.warn('[preload] Chunk failed to load while offline; not reloading', {
      error: messageOf(error),
    });
    return false;
  }
  const { storage } = env;
  const storageUnavailable = (reason?: unknown) => {
    logger.warn('[preload] Chunk failed to load and storage is unavailable; not reloading', {
      error: messageOf(error),
      ...(reason === undefined ? {} : { storageError: messageOf(reason) }),
    });
    return false;
  };
  if (!storage) return storageUnavailable();
  let last: number;
  try {
    last = Number(storage.getItem(PRELOAD_RELOAD_KEY));
  } catch (reason) {
    // A blocked or full store throws from the call itself; without the guard, no reload.
    return storageUnavailable(reason);
  }
  if (last > 0 && env.now() - last < PRELOAD_RELOAD_WINDOW_MS) {
    logger.error('[preload] Chunk still failing after a reload; leaving it to the error boundary', {
      error: messageOf(error),
      reloadedAt: new Date(last).toISOString(),
    });
    return false;
  }
  try {
    storage.setItem(PRELOAD_RELOAD_KEY, String(env.now()));
  } catch (reason) {
    // Unrecorded, the reload could not be told from the next one: no guard, so no reload.
    return storageUnavailable(reason);
  }
  logger.warn('[preload] Chunk failed to load; reloading for the current deployment', {
    error: messageOf(error),
  });
  env.reload();
  return true;
}

/**
 * @desc Wires {@link handlePreloadError} to `window`'s `vite:preloadError`. Called once from
 * `main.tsx`, before the app renders, so a failure in the very first lazy chunk is covered.
 * @returns the listener's removal, for tests.
 */
export function installPreloadErrorReload(target: Window = window): () => void {
  const listener = (event: VitePreloadErrorEvent) => {
    handlePreloadError(event.payload);
  };
  target.addEventListener('vite:preloadError', listener);
  return () => target.removeEventListener('vite:preloadError', listener);
}
